import type {
  ShopSearchRequest,
  StarterCollectionSearchRequest,
  WAlbumView,
  WCardSuiteSearchRequest,
  WCardSuiteSearchView,
  WCategoryContentView,
  WCategoryView,
  WCollectionView,
  WPartnerBoutiqueView,
  WShopSearchResultView,
} from '@zola/svc-web-api-ts-client';
import ApiService from '../util/apiService';
import type { AppDispatch } from './types';
import {
  receiveAlbums,
  receiveCategoryTree,
  receivedProductBySearch,
  receivePaperBySearch,
  requestCategoryTree,
  requestProductBySearch,
} from './types/ShopActionTypes';

const fetchCategoryTree = (key: string | null) => (dispatch: AppDispatch) => {
  dispatch(requestCategoryTree());
  const param = key ? `?categoryKey=${key}` : '';
  return ApiService.get(`/web-registry-api/v1/shop/category-tree${param}`).then(
    (response: WCategoryView[]) => dispatch(receiveCategoryTree(response))
  );
};

const fetchFeaturedCategoriesAndBrands = (key: string, includeCollections = false) => {
  const param = `?categoryKey=${key}&includeCollections=${includeCollections}`;
  return ApiService.get(`/web-registry-api/v1/shop/category-content${param}`).then(
    (response: WCategoryContentView) => response
  );
};

const requestAlbums = () => (dispatch: AppDispatch) => {
  return ApiService.get('/web-api/v1/photobook/catalog/album/list').then((json: WAlbumView[]) => {
    return dispatch(receiveAlbums(json));
  });
};

const requestPaper = (body: WCardSuiteSearchRequest) => (dispatch: AppDispatch) => {
  return ApiService.post('/web-api/v1/card-catalog/search/faceted', body).then(
    (json: WCardSuiteSearchView) => {
      return dispatch(receivePaperBySearch(json));
    }
  );
};

export type ShopSearchRequestBody = Omit<
  ShopSearchRequest,
  'platform_type' | 'ip_address' | 'user_agent' | 'store' | 'user_id'
>;

const requestProducts = (body: ShopSearchRequestBody) => (dispatch: AppDispatch) => {
  dispatch(requestProductBySearch());
  return ApiService.post('/web-registry-api/v1/shop/search', body).then(
    (json: WShopSearchResultView) => dispatch(receivedProductBySearch(json))
  );
};

const searchAllProducts = (body: ShopSearchRequestBody) =>
  ApiService.post('/web-registry-api/v1/shop/search', body).then(
    (json: WShopSearchResultView) => json
  );

const searchApproximate = (body: ShopSearchRequestBody) =>
  ApiService.post('/web-registry-api/v1/shop/approximate', body).then(
    (json: WShopSearchResultView) => json
  );

const fetchCollectionHeroContent = (key: string) =>
  ApiService.get(`/web-registry-api/v1/shop/collection-hero-content/${key}`).then(
    (json: WCollectionView) => json
  );

const fetchPartnerBoutiqueHeroContent = (key: string) =>
  ApiService.get(`/web-registry-api/v1/shop/partner-boutique-hero-content/${key}`).then(
    (json: WPartnerBoutiqueView) => json
  );

const searchCollectionByKey = (
  key: string,
  body: Omit<StarterCollectionSearchRequest, 'user_id'>
) =>
  ApiService.post(`/web-registry-api/v1/shop/collection/${key}/search`, body).then(
    (json: WShopSearchResultView) => json
  );

const searchCategoryByKey = (key: string, body: ShopSearchRequestBody) =>
  ApiService.post(`/web-registry-api/v1/shop/category/${key}/search`, body).then(
    (json: WShopSearchResultView) => json
  );

const searchFeaturedBrandsByKey = (key: string, body: ShopSearchRequestBody) =>
  ApiService.post(`/web-registry-api/v1/shop/brand/${key}/search`, body).then(
    (json: WShopSearchResultView) => json
  );

const searchPartnerBoutiqueByKey = (key: string, body: ShopSearchRequestBody) =>
  ApiService.post(`/web-registry-api/v1/shop/partner-boutique/${key}/search`, body).then(
    (json: WShopSearchResultView) => json
  );

export {
  fetchCategoryTree,
  fetchCollectionHeroContent,
  fetchPartnerBoutiqueHeroContent,
  fetchFeaturedCategoriesAndBrands,
  searchAllProducts,
  requestAlbums,
  requestPaper,
  requestProducts,
  searchApproximate,
  searchCollectionByKey,
  searchCategoryByKey,
  searchFeaturedBrandsByKey,
  searchPartnerBoutiqueByKey,
};
